import { SearchIcon } from "@chakra-ui/icons";
import { Input, InputGroup, InputLeftElement } from "@chakra-ui/react";
import React, { useState } from "react";
import { useSearchProperties } from "@tether-web-portal/api/properties/hooks";
import { useDebounce } from "@tether-web-portal/hooks/useDebounce";
import { Property } from "@tether-web-portal/types/Property";
import { NavItem } from "../sidebar/NavItem";
import { GloablPropertySearchModal } from "./GlobalPropertySearchModal";
import { useNavigate } from "react-router-dom";

interface GlobalPropertySearchProps {
  isCollapsed: boolean;
}

export const GlobalPropertySearch = ({ isCollapsed }: GlobalPropertySearchProps) => {
  const [queryPropertiesEnabled, setEnableQueryProperties] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 300);

  // A search box that searches on the SERVER. It used to download every property in the
  // organisation the first time the box was opened and match against them locally, which
  // is the whole portfolio on the wire to find one address.
  const { items: properties = [], isFetching, error: searchError } = useSearchProperties(debouncedSearch, {
    enabled: queryPropertiesEnabled,
  });

  // The rows on screen belong to `debouncedSearch`; the input shows `searchText`. Between
  // the two (300ms of debounce plus a round trip) a fast typist can press Enter on rows
  // that match a PREFIX of what they typed and open the wrong property — the local filter
  // this replaced re-ran synchronously on every keystroke, so that window did not exist.
  const resultsAreStale = searchText.trim() !== debouncedSearch.trim() || isFetching;

  const handleOpenProperty = (property: Property) => {
    navigate(`/property/${property.id}`);
  };

  const groupStyle = !isCollapsed
    ? {}
    : {
        width: "40px",
        marginleft: "2px",
      };

  const searchStyle = !isCollapsed
    ? {}
    : {
        WebkitPaddingEnd: 0,
        paddingInlineEnd: 0,
      };

  const handleOpenModalFromInputEvent = (
    e:
      | React.FocusEvent<HTMLElement>
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setEnableQueryProperties(true);
    setIsOpen(true);

    try {
      (e.target as HTMLElement).blur();
    } catch (error) {
      console.log("Could not blur search", error);
    }
  };

  return (
    <>
      {!isCollapsed && (
        <InputGroup style={groupStyle} borderColor="gray.800" mb={1}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            type="search"
            style={searchStyle}
            onClick={handleOpenModalFromInputEvent}
            onKeyPress={handleOpenModalFromInputEvent}
            value=""
            placeholder="Search Properties..."
            readOnly
          />
        </InputGroup>
      )}

      {isCollapsed && (
        <NavItem
          isCollapsed={isCollapsed}
          disabled={false}
          active={false}
          onClick={handleOpenModalFromInputEvent}
          icon={<SearchIcon />}
          label={"Search"}
        />
      )}
      <GloablPropertySearchModal
        properties={properties}
        resultsAreStale={resultsAreStale}
        // Every keystroke here is now a request; before this branch the list was fetched
        // once at mount. A 500 left the palette rendering an empty list, which reads as
        // "no property is called that" and offers no way to retry.
        searchError={Boolean(searchError)}
        onSearchChange={setSearchText}
        onItemSelected={handleOpenProperty}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onOpen={() => {
          setIsOpen(true);
          setEnableQueryProperties(true);
        }}
      />
    </>
  );
};
